import React, { useState, useEffect, useCallback } from 'react';
import { useAuthFetch } from '../../hooks/useAuthFetch';
import '../AdminPage.css';

const RANGE_OPTIONS = [
  { label: 'Last 7 days', days: 7 },
  { label: 'Last 30 days', days: 30 },
  { label: 'Last 90 days', days: 90 },
];

function useAdminFetch(url, days) {
  const { authFetch } = useAuthFetch();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notConfigured, setNotConfigured] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    setNotConfigured(false);
    authFetch(`${process.env.REACT_APP_API_URL}${url}?days=${days}`)
      .then(res => {
        if (res.status === 501) {
          setNotConfigured(true);
          setLoading(false);
          return null;
        }
        if (!res.ok) throw new Error(`Server returned ${res.status}`);
        return res.json();
      })
      .then(json => {
        if (json !== null) setData(json);
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setError('Failed to load data.');
        setLoading(false);
      });
  }, [url, days]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  return { data, loading, error, notConfigured };
}

function ProjectionCard({ openaiTotal, azureTotal, anthropicTotal, days }) {
  if (openaiTotal === null && azureTotal === null && anthropicTotal === null) return null;

  const today = new Date();
  const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
  const dayOfYear = Math.floor((today - new Date(today.getFullYear(), 0, 0)) / 86400000);
  const daysInYear = (today.getFullYear() % 4 === 0) ? 366 : 365;
  const daysRemainingInYear = daysInYear - dayOfYear;

  const monthName = today.toLocaleString('default', { month: 'long' });

  const fmt = (v) => v != null ? `$${v.toFixed(2)}` : '—';

  const runRate = (total) => total != null ? (total / days) * 30 : null;
  const endOfMonth = (total) => total != null ? (total / days) * daysInMonth : null;
  const annualCost = (total) => total != null ? (total / days) * 365 : null;
  const remainingYear = (total) => total != null ? (total / days) * daysRemainingInYear : null;

  const combined = (fn) => {
    const a = fn(openaiTotal);
    const b = fn(azureTotal);
    const c = fn(anthropicTotal);
    if (a === null && b === null && c === null) return null;
    return (a ?? 0) + (b ?? 0) + (c ?? 0);
  };

  return (
    <div className="costs-projection">
      <div className="costs-section-title">
        Cost Projections
        <span className="costs-projection-subtitle"> · based on last {days} days</span>
      </div>
      <div className="costs-projection-grid">
        <div />
        <div className="costs-projection-colhead">OpenAI</div>
        <div className="costs-projection-colhead">Azure</div>
        <div className="costs-projection-colhead">Anthropic</div>
        <div className="costs-projection-colhead">Combined</div>

        <div className="costs-projection-label">Monthly run rate</div>
        <div className="costs-projection-value">{fmt(runRate(openaiTotal))}</div>
        <div className="costs-projection-value">{fmt(runRate(azureTotal))}</div>
        <div className="costs-projection-value">{fmt(runRate(anthropicTotal))}</div>
        <div className="costs-projection-value costs-projection-value--combined">
          {fmt(combined(runRate))}{combined(runRate) != null && <span className="costs-projection-unit"> /mo</span>}
        </div>

        <div className="costs-projection-label">Est. end of {monthName}</div>
        <div className="costs-projection-value">{fmt(endOfMonth(openaiTotal))}</div>
        <div className="costs-projection-value">{fmt(endOfMonth(azureTotal))}</div>
        <div className="costs-projection-value">{fmt(endOfMonth(anthropicTotal))}</div>
        <div className="costs-projection-value costs-projection-value--combined">
          {fmt(combined(endOfMonth))}
        </div>

        <div className="costs-projection-label">Est. annual cost</div>
        <div className="costs-projection-value">{fmt(annualCost(openaiTotal))}</div>
        <div className="costs-projection-value">{fmt(annualCost(azureTotal))}</div>
        <div className="costs-projection-value">{fmt(annualCost(anthropicTotal))}</div>
        <div className="costs-projection-value costs-projection-value--combined">
          {fmt(combined(annualCost))}{combined(annualCost) != null && <span className="costs-projection-unit"> /yr</span>}
        </div>

        <div className="costs-projection-label">Remaining this year</div>
        <div className="costs-projection-value">{fmt(remainingYear(openaiTotal))}</div>
        <div className="costs-projection-value">{fmt(remainingYear(azureTotal))}</div>
        <div className="costs-projection-value">{fmt(remainingYear(anthropicTotal))}</div>
        <div className="costs-projection-value costs-projection-value--combined">
          {fmt(combined(remainingYear))}
        </div>
      </div>
    </div>
  );
}

function InternalSection({ days }) {
  const { data, loading, error, notConfigured } = useAdminFetch('/api/admin/costs/internal', days);

  const rows = data?.rows || [];
  const totalsByEndpoint = rows.reduce((acc, row) => {
    acc[row.endpoint] = (acc[row.endpoint] || 0) + row.requests;
    return acc;
  }, {});

  return (
    <div className="costs-section">
      <div className="costs-section-title">AI Request Volume</div>
      {loading && <p className="costs-loading">Loading…</p>}
      {error && <p className="costs-error">{error}</p>}
      {notConfigured && <p className="costs-not-configured">Not configured</p>}
      {!loading && !error && !notConfigured && rows.length === 0 && (
        <p className="costs-empty">No AI usage recorded for this period.</p>
      )}
      {!loading && !error && !notConfigured && rows.length > 0 && (
        <>
          <div className="costs-table-wrapper">
            <table className="costs-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Endpoint</th>
                  <th>Requests</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={i}>
                    <td>{row.day}</td>
                    <td>{row.endpoint}</td>
                    <td>{row.requests}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="costs-summary">
            {Object.entries(totalsByEndpoint).map(([ep, total]) => (
              <span key={ep} style={{ marginLeft: '1rem' }}>
                {ep}: <strong>{total}</strong> requests
              </span>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function OpenAISection({ days, onTotal }) {
  const { data, loading, error, notConfigured } = useAdminFetch('/api/admin/costs/openai', days);

  // OpenAI org costs API returns { data: [{ start_time, end_time, results: [{ amount, line_item }] }] }
  const buckets = data?.data || [];
  const rows = buckets.flatMap(bucket => {
    const date = new Date(bucket.start_time * 1000).toLocaleDateString();
    return (bucket.results || []).map(r => ({
      date,
      lineItem: r.line_item || '—',
      cost: r.amount?.value != null ? `$${parseFloat(r.amount.value).toFixed(4)}` : '—',
    }));
  });
  const totalCost = buckets.reduce((sum, bucket) =>
    sum + (bucket.results || []).reduce((s, r) => s + parseFloat(r.amount?.value || 0), 0), 0);

  useEffect(() => {
    if (!loading && !notConfigured && !error) {
      onTotal?.(totalCost);
    }
  }, [loading, notConfigured, error, totalCost, onTotal]);

  return (
    <div className="costs-section">
      <div className="costs-section-title">OpenAI Costs</div>
      {loading && <p className="costs-loading">Loading…</p>}
      {error && <p className="costs-error">{error}</p>}
      {notConfigured && <p className="costs-not-configured">Not configured — set OPENAI_ORG_KEY to enable.</p>}
      {!loading && !error && !notConfigured && rows.length === 0 && (
        <p className="costs-empty">No OpenAI costs for this period.</p>
      )}
      {!loading && !error && !notConfigured && rows.length > 0 && (
        <>
          <div className="costs-table-wrapper">
            <table className="costs-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Line Item</th>
                  <th>Cost</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={i}>
                    <td>{row.date}</td>
                    <td>{row.lineItem}</td>
                    <td>{row.cost}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="costs-summary">
            Total: <strong>${totalCost.toFixed(4)}</strong>
          </div>
        </>
      )}
    </div>
  );
}

function AzureSection({ days, onTotal }) {
  const { data, loading, error, notConfigured } = useAdminFetch('/api/admin/costs/azure', days);

  // Azure Cost Management returns properties.rows: [[cost, currency, serviceName], ...]
  // and properties.columns: [{name, type}, ...]
  const properties = data?.properties || {};
  const columns = (properties.columns || []).map(c => c.name);
  const rawRows = properties.rows || [];

  const costIdx = columns.indexOf('Cost');
  const currencyIdx = columns.indexOf('Currency');
  const serviceIdx = columns.indexOf('ServiceName');

  const rows = rawRows.map(row => ({
    service: serviceIdx >= 0 ? row[serviceIdx] : '—',
    cost: costIdx >= 0 ? row[costIdx] : null,
    currency: currencyIdx >= 0 ? row[currencyIdx] : 'USD',
  }));

  const totalCost = rows.reduce((sum, r) => sum + (r.cost || 0), 0);
  const currency = rows[0]?.currency || 'USD';

  useEffect(() => {
    if (!loading && !notConfigured && !error) {
      onTotal?.(totalCost);
    }
  }, [loading, notConfigured, error, totalCost, onTotal]);

  return (
    <div className="costs-section">
      <div className="costs-section-title">Azure Costs</div>
      {loading && <p className="costs-loading">Loading…</p>}
      {error && <p className="costs-error">{error}</p>}
      {notConfigured && (
        <p className="costs-not-configured">
          Not configured — set AZURE_SUBSCRIPTION_ID, AZURE_SP_CLIENT_ID, AZURE_SP_CLIENT_SECRET, and AZURE_SP_TENANT_ID to enable.
        </p>
      )}
      {!loading && !error && !notConfigured && rows.length === 0 && (
        <p className="costs-empty">No Azure costs for this period.</p>
      )}
      {!loading && !error && !notConfigured && rows.length > 0 && (
        <>
          <div className="costs-table-wrapper">
            <table className="costs-table">
              <thead>
                <tr>
                  <th>Service</th>
                  <th>Cost</th>
                  <th>Currency</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={i}>
                    <td>{row.service}</td>
                    <td>${(row.cost || 0).toFixed(4)}</td>
                    <td>{row.currency}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="costs-summary">
            Total: <strong>${totalCost.toFixed(4)} {currency}</strong>
          </div>
        </>
      )}
    </div>
  );
}

function AnthropicSection({ days, onTotal }) {
  const { data, loading, error, notConfigured } = useAdminFetch('/api/admin/costs/anthropic', days);

  // Anthropic cost report: { data: [{ starting_at, ending_at, results: [{ amount, currency, description }] }] }
  // Note: amount is a flat string in cents (e.g. "701.4" = $7.014), not dollars.
  const buckets = data?.data || [];
  const rows = buckets.flatMap(bucket => {
    const date = new Date(bucket.starting_at).toLocaleDateString();
    return (bucket.results || []).map(r => ({
      date,
      model: r.description || '—',
      cost: r.amount != null ? `$${(parseFloat(r.amount) / 100).toFixed(4)}` : '—',
    }));
  });
  const totalCost = buckets.reduce((sum, bucket) =>
    sum + (bucket.results || []).reduce((s, r) => s + parseFloat(r.amount || 0) / 100, 0), 0);

  useEffect(() => {
    if (!loading && !notConfigured && !error) {
      onTotal?.(totalCost);
    }
  }, [loading, notConfigured, error, totalCost, onTotal]);

  return (
    <div className="costs-section">
      <div className="costs-section-title">Anthropic Costs</div>
      {loading && <p className="costs-loading">Loading…</p>}
      {error && <p className="costs-error">{error}</p>}
      {notConfigured && <p className="costs-not-configured">Not configured — set ANTHROPIC_ADMIN_KEY to enable.</p>}
      {!loading && !error && !notConfigured && rows.length === 0 && (
        <p className="costs-empty">No Anthropic costs for this period.</p>
      )}
      {!loading && !error && !notConfigured && rows.length > 0 && (
        <>
          <div className="costs-table-wrapper">
            <table className="costs-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Model</th>
                  <th>Cost</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={i}>
                    <td>{row.date}</td>
                    <td>{row.model}</td>
                    <td>{row.cost}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="costs-summary">
            Total: <strong>${totalCost.toFixed(4)}</strong>
          </div>
        </>
      )}
    </div>
  );
}

export default function CostsTab() {
  const [days, setDays] = useState(30);
  const [openaiTotal, setOpenaiTotal] = useState(null);
  const [azureTotal, setAzureTotal] = useState(null);
  const [anthropicTotal, setAnthropicTotal] = useState(null);

  const handleDaysChange = (newDays) => {
    setDays(newDays);
    setOpenaiTotal(null);
    setAzureTotal(null);
    setAnthropicTotal(null);
  };

  return (
    <div>
      <ProjectionCard openaiTotal={openaiTotal} azureTotal={azureTotal} anthropicTotal={anthropicTotal} days={days} />

      <div className="costs-date-range">
        {RANGE_OPTIONS.map(opt => (
          <button
            key={opt.days}
            className={`costs-range-btn${days === opt.days ? ' costs-range-btn--active' : ''}`}
            onClick={() => handleDaysChange(opt.days)}
          >
            {opt.label}
          </button>
        ))}
      </div>

      <InternalSection days={days} />
      <OpenAISection days={days} onTotal={setOpenaiTotal} />
      <AzureSection days={days} onTotal={setAzureTotal} />
      <AnthropicSection days={days} onTotal={setAnthropicTotal} />
    </div>
  );
}
